import React, { useState, useEffect, useMemo } from "react";
import "./ModalStockUpdate.scss";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  TextField,
  IconButton,
  Button,
  Checkbox,
  Chip,
  MenuItem,
  Select,
  FormControl,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import moment from "moment";
import { momentGuardado } from "../../../shared/util/datasGuardadas";
import useLocalizacoes from "./useLocalizacoes";

// ─── Select encadeado Armazém → Localização (versão compacta para tabela) ────
function LocSelectInline({
  lote,
  armazens,
  localizacoesPorArmazem,
  onArmazem,
  onLocalizacao,
}) {
  const locs = localizacoesPorArmazem(lote.armazem || "");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <Select
        size="small"
        value={lote.armazem || ""}
        onChange={(e) => onArmazem(e.target.value)}
        displayEmpty
        style={{ fontSize: 12, minWidth: 120 }}
      >
        <MenuItem value="" disabled>
          <em>Armazém</em>
        </MenuItem>
        {armazens.map((a) => (
          <MenuItem key={a.id} value={a.id} style={{ fontSize: 12 }}>
            {a.codigo} — {a.nome}
          </MenuItem>
        ))}
      </Select>

      <Select
        size="small"
        value={lote.localizacao || ""}
        onChange={(e) => onLocalizacao(e.target.value)}
        displayEmpty
        disabled={!lote.armazem || locs.length === 0}
        style={{ fontSize: 12, minWidth: 120 }}
      >
        <MenuItem value="" disabled>
          <em>Localização</em>
        </MenuItem>
        {locs.map((l) => (
          <MenuItem key={l.id} value={l.id} style={{ fontSize: 12 }}>
            {l.codigo} — {l.nome}
          </MenuItem>
        ))}
      </Select>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
function ModalStockUpdate({
  handleSubmitUpdateStock,
  handleCloseModalAdicionarStock,
  stock,
  categoriasStock,
  clinicaId,
  sendRequest,
  auth,
  produtoInicial,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [erro, setErro] = useState("");
  const [textQuery, setTextQuery] = useState("");
  const [categoria, setCategoria] = useState("todas");
  const [step, setStep] = useState(produtoInicial ? 2 : 1);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [fornecedoresList, setFornecedoresList] = useState([]);
  const [stockData, setStockData] = useState([]);

  // Localizações reais
  const { armazens, localizacoes, localizacoesPorArmazem, primeiraLocalizacao } =
    useLocalizacoes({ sendRequest, clinicaId, auth });

  const defaultArmazem = armazens[0]?.id || "";
  const defaultLocalizacao = defaultArmazem
    ? primeiraLocalizacao(defaultArmazem)?.id || ""
    : "";

  /* ──────────────────────────────────────────────────────────────────────────
   * Onde este produto já está guardado.
   *
   * A entrada de um produto que já existe vinha com o primeiro armazém da
   * lista e a primeira localização dele — que raramente é onde o produto está.
   * Quem dava a entrada tinha de corrigir os dois selects à mão, e quando se
   * esquecia o mesmo produto passava a viver em duas prateleiras: as contagens
   * por localização deixavam de bater certo e ninguém percebia porquê.
   *
   * Escolhe-se a localização com MAIS unidades, e não a do lote mais recente.
   * O lote mais recente pode ter sido uma entrada pequena feita no sítio
   * errado — precisamente o engano que isto vem evitar — enquanto o grosso das
   * unidades está onde o produto realmente vive.
   *
   * Lotes esgotados não contam: já não está lá nada. E confirma-se sempre
   * contra a lista de localizações ativas, porque é de lá que saem os valores
   * dos selects; uma localização desativada entretanto não daria opção
   * nenhuma e o select ficaria em branco.
   * ────────────────────────────────────────────────────────────────────────── */
  // O id tanto pode vir cru como dentro do documento populado.
  const idDe = (valor) => {
    if (!valor) return "";
    if (typeof valor === "object") return String(valor.id || valor._id || "");
    return String(valor);
  };

  const localizacaoHabitual = (item) => {
    if (!item?.lotes?.length || localizacoes.length === 0) return null;

    const totalPorLocalizacao = new Map();
    for (const lote of item.lotes) {
      if (lote.esgotado) continue;
      for (const registo of lote.localizacoes || []) {
        const id = idDe(registo.localizacao);
        if (!id) continue;
        const acumulado = totalPorLocalizacao.get(id) || 0;
        totalPorLocalizacao.set(id, acumulado + (Number(registo.quantidade) || 0));
      }
    }
    if (totalPorLocalizacao.size === 0) return null;

    const [idMaisUsada] = [...totalPorLocalizacao.entries()].sort(
      (a, b) => b[1] - a[1],
    )[0];

    const loc = localizacoes.find((l) => idDe(l) === idMaisUsada);
    return loc ? { localizacao: idDe(loc), armazem: idDe(loc.armazem) } : null;
  };

  /** Os selects já preenchidos para um produto: os dele, ou os do costume. */
  const destinoInicial = (item) => {
    const habitual = localizacaoHabitual(item);
    return {
      armazem: habitual?.armazem || defaultArmazem,
      localizacao: habitual?.localizacao || defaultLocalizacao,
    };
  };

  useEffect(() => {
    if (stock && stock.stock) {
      setStockData(
        stock.stock.map((item) => ({
          ...item,
          lotes: item.lotes.map((lote) => ({
            ...lote,
            quantidadeAdicionada: 0,
            novoPrecoCompra: lote.precoCompra || 0,
            novoFornecedor: lote.fornecedor || "",
            novoLote: "",
          })),
        })),
      );
    }
  }, [stock]);

  // ADICIONAR — a seguir ao useEffect do stockData
  useEffect(() => {
    if (!produtoInicial || armazens.length === 0) return;
    // As localizações chegam no mesmo pedido que os armazéns, por isso este
    // efeito corre uma vez só. A guarda é para o caso de não correr: sem ela,
    // uma segunda passagem apagava a quantidade já escrita.
    if (selectedProducts.length > 0) return;

    const { armazem: arm, localizacao: loc } = destinoInicial(produtoInicial);

    setSelectedProducts([
      {
        ...produtoInicial,
        lotes: [
          {
            _id: "temp-" + produtoInicial.produto._id,
            quantidade: 0,
            quantidadeAdicionada: 0,
            novoPrecoCompra: 0,
            novoFornecedor: "",
            novoLote: "",
            armazem: arm,
            localizacao: loc,
            dataValidade: null,
            isTemporaryLot: true,
          },
        ],
      },
    ]);
  }, [armazens, localizacoes]);

  useEffect(() => {
    const fetchFornecedores = async () => {
      try {
        const res = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/fornecedores/clinica/stock/${clinicaId}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        );
        setFornecedoresList(res.fornecedores || []);
      } catch (err) {
        console.error(err);
      }
    };
    fetchFornecedores();
  }, [sendRequest, clinicaId, auth.token]);

  // ── Helpers de categoria ──
  const buildCategoryTree = (categories, parentId = null) => {
    if (!categories || !Array.isArray(categories)) return [];
    return categories
      .filter((c) =>
        parentId === null
          ? c.categoriaPai === null || c.categoriaPai === undefined
          : c.categoriaPai === parentId,
      )
      .map((c) => ({
        ...c,
        subcategorias: buildCategoryTree(categories, c.id),
      }));
  };

  const renderCategoryOptions = (categories, level = 0) => {
    if (!categories) return null;
    return categories.flatMap((c) => [
      <option
        key={c.id}
        value={c.id}
        style={{
          paddingLeft: `${level * 20}px`,
          fontWeight: level === 0 ? "bold" : "normal",
        }}
      >
        {level > 0 ? `${"\u00A0".repeat(level * 2)}└─ ${c.nome}` : c.nome}
      </option>,
      ...(c.subcategorias
        ? renderCategoryOptions(c.subcategorias, level + 1)
        : []),
    ]);
  };

  const categoriesTree = useMemo(
    () => buildCategoryTree(categoriasStock),
    [categoriasStock],
  );

  // ── Pesquisa ──
  const handleSearch = () => {
    let results = stockData;
    if (categoria !== "todas") {
      results = results.filter((st) => st.produto.categoriaStock === categoria);
    }
    if (textQuery.trim()) {
      results = results.filter((item) =>
        item.produto.nome.toLowerCase().includes(textQuery.toLowerCase()),
      );
    }
    results.sort((a, b) => a.produto.nome.localeCompare(b.produto.nome));
    setSearchResults(results);
  };

  const clearSearch = () => {
    setTextQuery("");
    setCategoria("todas");
    setSearchResults([]);
  };

  useEffect(() => {
    if (textQuery.trim() || categoria !== "todas") {
      handleSearch();
    } else {
      setSearchResults([]);
    }
  }, [textQuery, categoria]);

  // ── Selecção de produtos ──
  const toggleProductSelection = (product) => {
    const isSelected = selectedProducts.some(
      (p) => p.produto._id === product.produto._id,
    );
    if (isSelected) {
      setSelectedProducts(
        selectedProducts.filter((p) => p.produto._id !== product.produto._id),
      );
      return;
    }

    const { armazem: arm, localizacao: loc } = destinoInicial(product);

    const lotesParaAdicionar = [
      {
        _id: "temp-" + product.produto._id,
        quantidade: 0,
        quantidadeAdicionada: 0,
        novoPrecoCompra: 0,
        novoFornecedor: "",
        novoLote: "",
        armazem: arm,
        localizacao: loc,
        dataValidade: null,
        isTemporaryLot: true,
      },
    ];

    setSelectedProducts([
      ...selectedProducts,
      { ...product, lotes: lotesParaAdicionar },
    ]);
  };

  const isProductSelected = (id) =>
    selectedProducts.some((p) => p.produto._id === id);

  // ── Handlers de campo ──
  const updateLote = (productId, loteIndex, field, value) => {
    setSelectedProducts((prev) =>
      prev.map((item) => {
        if (item.produto._id !== productId) return item;
        return {
          ...item,
          lotes: item.lotes.map((lote, i) =>
            i === loteIndex ? { ...lote, [field]: value } : lote,
          ),
        };
      }),
    );
  };

  const handleArmazemChange = (productId, loteIndex, armazemId) => {
    const loc = primeiraLocalizacao(armazemId)?.id || "";
    updateLote(productId, loteIndex, "armazem", armazemId);
    updateLote(productId, loteIndex, "localizacao", loc);
  };

  const handleLocalizacaoChange = (productId, loteIndex, locId) => {
    updateLote(productId, loteIndex, "localizacao", locId);
  };

  // ── Submit ──
  const preSubmit = () => {
    const produtosModificados = selectedProducts.filter((item) =>
      item.lotes.some((lote) => lote.quantidadeAdicionada > 0),
    );

    if (produtosModificados.length === 0) {
      setErro("Nenhuma alteração foi feita no stock.");
      return;
    }

    // Verificar localizações
    const semLocalizacao = produtosModificados.some((item) =>
      item.lotes.some(
        (l) => l.quantidadeAdicionada > 0 && (!l.armazem || !l.localizacao),
      ),
    );
    if (semLocalizacao) {
      setErro(
        "Por favor, selecione armazém e localização para todos os produtos.",
      );
      return;
    }

    setIsSubmitting(true);
    handleSubmitUpdateStock(produtosModificados);
  };

  const avancarParaEdicao = () => {
    if (selectedProducts.length === 0) {
      setErro("Selecione pelo menos um produto para continuar.");
      return;
    }
    setErro("");
    setStep(2);
  };

  const totalSelecionado = selectedProducts.length;
  const totalAlteracoes = selectedProducts.reduce(
    (acc, item) =>
      acc + item.lotes.filter((l) => l.quantidadeAdicionada > 0).length,
    0,
  );

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <div className="stockCadastrar__container">
        {/* Passos */}
        <div className="stepHeader">
          <div className={`step ${step === 1 ? "active" : "completed"}`}>
            <div className="stepNumber">1</div>
            <div className="stepTitle">Seleção de Produtos</div>
          </div>
          <div className="stepConnector"></div>
          <div className={`step ${step === 2 ? "active" : ""}`}>
            <div className="stepNumber">2</div>
            <div className="stepTitle">Edição de Quantidades</div>
          </div>
        </div>

        {erro && <div className="stockUpdate__erro">{erro}</div>}

        {/* ── Passo 1: Selecção ── */}
        {step === 1 && (
          <div className="selectionStep">
            <div className="stockUpdate__select-container">
              <select
                onChange={(e) => setCategoria(e.target.value)}
                className="select__filter-categoria"
                value={categoria}
              >
                <option value="todas">Todas Categorias</option>
                {renderCategoryOptions(categoriesTree)}
              </select>

              <div className="stock__header__container2__container2 textBoxSearch__container">
                <input
                  type="text"
                  className="textBoxSearch__input"
                  placeholder="Pesquisar produtos..."
                  value={textQuery}
                  onChange={(e) => setTextQuery(e.target.value)}
                />
                <SearchIcon className="financeiro__filtros__search-icon" />
              </div>

              <Button
                variant="outlined"
                onClick={clearSearch}
                startIcon={<ClearIcon />}
              >
                Limpar
              </Button>
            </div>

            <div className="selectionCounter">
              <Chip
                icon={<AddShoppingCartIcon />}
                label={`${totalSelecionado} produtos selecionados`}
                color={totalSelecionado > 0 ? "primary" : "default"}
              />
              {totalSelecionado > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={avancarParaEdicao}
                  endIcon={<ArrowForwardIcon />}
                >
                  Continuar para Edição
                </Button>
              )}
            </div>

            <div className="searchResults">
              {searchResults.length === 0 ? (
                <div className="noResults">
                  {textQuery || categoria !== "todas"
                    ? "Nenhum produto encontrado."
                    : "Use os filtros acima para buscar produtos."}
                </div>
              ) : (
                <div className="productList">
                  <div className="productList__header">
                    <div className="productList__check"></div>
                    <div className="productList__name">Produto</div>
                    <div className="productList__category">Categoria</div>
                    <div className="productList__quantity">Qtd Atual</div>
                    <div className="productList__ideal">Stock min</div>
                    <div className="productList__lotes">Lotes</div>
                  </div>

                  {searchResults.map((item) => (
                    <div
                      key={item.produto._id}
                      className={`productList__item ${isProductSelected(item.produto._id) ? "selected" : ""}`}
                      onClick={() => toggleProductSelection(item)}
                    >
                      <div className="productList__check">
                        <Checkbox
                          checked={isProductSelected(item.produto._id)}
                          onChange={() => {}}
                        />
                      </div>
                      <div className="productList__name">
                        {item.produto.nome}
                        {item.produto.classificacao === "MP" && (
                          <span className="badge materiaPrima">
                            Matéria Prima
                          </span>
                        )}
                      </div>
                      <div className="productList__category">
                        {categoriasStock?.find(
                          (c) => c.id === item.produto.categoriaStock,
                        )?.nome || "-"}
                      </div>
                      <div className="productList__quantity">
                        {item.quantidadeTotal} {item.produto.unidadeMedida}
                      </div>
                      <div className="productList__ideal">
                        {item.quantidadeIdeal} {item.produto.unidadeMedida}
                      </div>
                      <div className="productList__lotes">
                        {item.lotes.filter((l) => !l.esgotado).length} lote(s)
                        ativos
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Passo 2: Edição ── */}
        {step === 2 && (
          <div className="editStep">
            <div className="editStep__header">
              <Button
                variant="outlined"
                onClick={() => setStep(1)}
                startIcon={<ArrowBackIcon />}
              >
                Voltar para Seleção
              </Button>
              <Chip
                icon={<CheckCircleIcon />}
                label={`${totalAlteracoes} alterações a realizar`}
                color={totalAlteracoes > 0 ? "success" : "default"}
                variant="outlined"
              />
            </div>

            <div className="editStep__title">
              Adicionar Novos Lotes ao Estoque
            </div>
            <div className="editStep__subtitle">
              Preencha as informações para os novos lotes dos produtos
              selecionados
            </div>

            <div className="stockUpdate__table">
              {/* Cabeçalho */}
              <div className="stockUpdate__table__row stockUpdate__table__row--header">
                <span>Produto</span>
                <span>Em stock</span>
                <span>Stock min</span>
                <span>Adicionar</span>
                <span>Lote</span>
                <span>Preço</span>
                <span>Fornecedor</span>
                <span>Validade</span>
                <span>Armazém / Loc.</span>
              </div>

              {selectedProducts.map((item, index) => (
                <div className="stockUpdate__table__row" key={index + "ABC"}>
                  <span className="produto-nome">
                    {item.produto.nome}
                    <span className="add-badge">Novo</span>
                    {item.produto.classificacao === "MP" && (
                      <span className="badge materiaPrima">MP</span>
                    )}
                  </span>

                  <span className="readonly-value">
                    {Number.isInteger(item.quantidadeTotal)
                      ? item.quantidadeTotal
                      : parseFloat(item.quantidadeTotal).toFixed(2)}{" "}
                    {item.produto.unidadeMedida}
                  </span>

                  <span className="readonly-value">
                    {item.quantidadeIdeal} {item.produto.unidadeMedida}
                  </span>

                  {/* Quantidade */}
                  <TextField
                    className="stockUpdate__table__quantity-input"
                    type="number"
                    value={item.lotes[0].quantidadeAdicionada || 0}
                    onChange={(e) =>
                      updateLote(
                        item.produto._id,
                        0,
                        "quantidadeAdicionada",
                        Math.max(0, Number(e.target.value)),
                      )
                    }
                    inputProps={{ min: 0, step: "0.01" }}
                    size="small"
                  />

                  {/* Lote */}
                  <TextField
                    style={{ padding: "5px" }}
                    placeholder="Auto-gerado"
                    value={item.lotes[0].novoLote || ""}
                    onChange={(e) =>
                      updateLote(
                        item.produto._id,
                        0,
                        "novoLote",
                        e.target.value,
                      )
                    }
                    size="small"
                  />

                  {/* Preço */}
                  <TextField
                    style={{ padding: "5px" }}
                    type="number"
                    value={item.lotes[0].novoPrecoCompra || 0}
                    onChange={(e) =>
                      updateLote(
                        item.produto._id,
                        0,
                        "novoPrecoCompra",
                        e.target.value,
                      )
                    }
                    inputProps={{ min: 0, step: "0.01" }}
                    size="small"
                  />

                  {/* Fornecedor */}
                  <FormControl
                    size="small"
                    style={{ padding: "5px", minWidth: 130 }}
                  >
                    <Select
                      value={item.lotes[0].novoFornecedor || ""}
                      onChange={(e) =>
                        updateLote(
                          item.produto._id,
                          0,
                          "novoFornecedor",
                          e.target.value,
                        )
                      }
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Fornecedor</em>
                      </MenuItem>
                      {fornecedoresList.map((f) => (
                        <MenuItem key={f._id} value={f._id}>
                          {f.nome}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Validade */}
                  <div className="stockUpdate__table__date-container">
                    <DesktopDatePicker
                      inputFormat="DD-MM-YYYY"
                      value={momentGuardado(item.lotes[0].dataValidade)}
                      onChange={(date) =>
                        updateLote(
                          item.produto._id,
                          0,
                          "dataValidade",
                          date ? date.toDate() : null,
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          className="stockUpdate__table__date-field"
                        />
                      )}
                    />
                    <IconButton
                      size="small"
                      onClick={() =>
                        updateLote(item.produto._id, 0, "dataValidade", null)
                      }
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </div>

                  {/* Armazém / Localização — selects reais */}
                  <LocSelectInline
                    lote={item.lotes[0]}
                    armazens={armazens}
                    localizacoesPorArmazem={localizacoesPorArmazem}
                    onArmazem={(v) =>
                      handleArmazemChange(item.produto._id, 0, v)
                    }
                    onLocalizacao={(v) =>
                      handleLocalizacaoChange(item.produto._id, 0, v)
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Botões */}
        <div className="fluxo-atendimento__modal__bottom_btns">
          <span className="cancel-btn" onClick={handleCloseModalAdicionarStock}>
            Cancelar
          </span>
          {step === 2 && (
            <span
              disabled={isSubmitting || totalAlteracoes === 0}
              className={`blue-button ${isSubmitting || totalAlteracoes === 0 ? "disabled" : ""}`}
              onClick={preSubmit}
            >
              {isSubmitting ? "Guardando..." : "Atualizar Stock"}
            </span>
          )}
        </div>
      </div>
    </LocalizationProvider>
  );
}

export default ModalStockUpdate;
